import {
  AUTH_USER_LOCAL_STORAGE_KEY,
  getLoggingActions,
  LoginTypeEnum,
  logoutAuthUser,
  logoutSession,
  withCsrf,
  clearCsrfToken,
  MS_BACKEND_LOGIN_ATTEMPT_KEY,
  preserveTableColumn,
  SESSION_SLICE_SESSION_STORAGE_KEY,
  setForceMsalPromptNextLogin,
  showLoader,
  UserTypeEnum,
} from '@app-store'
import type { Persistor } from 'redux-persist'
import { AUTH_PROVIDER, getLoginCredentials } from '../utils/constants'
import { clearMsalAppSessionOnly } from '../utils/msalConfig'
import { clearMsalAccessTokenBridge } from '@ecrm-msal'
import {
  getPostLogoutCapturePath,
  setSessionPostLogoutReturnPath,
} from '../utils/postLogoutReturnPath'
import { broadcastForceLogout } from '@ecrm-session'
import type { AppDispatch } from '../utils/agent.store'

/**
 * `persist.flush()` only guarantees the ROOT persistor's own debounced write
 * completes — authUser (localStorage) and session (sessionStorage) are
 * NESTED persistReducers with their own independent write-back, which flush()
 * does not reliably wait for. Without this, a fast logout→re-login in the
 * same tab can navigate to the next page load before the cleared state
 * actually lands in storage, so rehydration picks up the stale pre-logout
 * token/profile — Branch B of the boot saga then treats that as "already
 * authenticated" and skips the whole getUserInfo → permission-loading chain.
 * This mirrors the same explicit-removeItem pattern already used in
 * `handleSessionExpiry` (agent.store.ts) for the session key.
 */
export function clearPersistedAuthStorage(): void {
  try {
    sessionStorage.removeItem(SESSION_SLICE_SESSION_STORAGE_KEY)
  } catch {
    /* ignore */
  }
  try {
    localStorage.removeItem(AUTH_USER_LOCAL_STORAGE_KEY)
  } catch {
    /* ignore */
  }
}

// Guards against re-entrant logout calls within one page's lifetime — e.g. a
// double-click on the logout button (handleLogoutClick has no debounce of its
// own), or a local click racing an incoming cross-tab force-logout broadcast.
// Both would otherwise independently run the full logout sequence (including
// a second, redundant /auth/logout/v2 call) since the function itself has no
// protection against concurrent invocation. Never reset: this module's
// lifetime ends at the navigation performAgentDeskLogout itself triggers, so
// there's no "next legitimate logout" to unblock before then.
let logoutInFlight: Promise<void> | null = null

export function performAgentDeskLogout(params: {
  dispatch: AppDispatch
  persist: Persistor
  token: string | null
  loginType: LoginTypeEnum
  /** Broadcast to other tabs (default true). */
  broadcastCrossTab?: boolean
  invalidateToken?: () => Promise<void>
}): Promise<void> {
  if (logoutInFlight) return logoutInFlight
  logoutInFlight = runLogout(params)
  return logoutInFlight
}

async function runLogout(params: {
  dispatch: AppDispatch
  persist: Persistor
  token: string | null
  loginType: LoginTypeEnum
  broadcastCrossTab?: boolean
  invalidateToken?: () => Promise<void>
}): Promise<void> {
  const {
    dispatch,
    persist,
    token,
    loginType,
    broadcastCrossTab = true,
    invalidateToken,
  } = params

  const authProvider = getLoginCredentials().authProvider

  if (broadcastCrossTab) {
    broadcastForceLogout(loginType)
  }

  if (token) {
    await getLoggingActions(token, loginType, UserTypeEnum.AGENT)
  }

  preserveTableColumn(persist)

  setSessionPostLogoutReturnPath(getPostLogoutCapturePath())

  if (authProvider === AUTH_PROVIDER.MSAL) {
    dispatch(showLoader())
    await clearMsalAppSessionOnly()
    clearMsalAccessTokenBridge()
    await invalidateToken?.()
    dispatch(logoutAuthUser())
    dispatch(setForceMsalPromptNextLogin(true))
    await persist.flush()
    clearPersistedAuthStorage()
    window.location.href = `${window.location.origin}/logout?forceMsalPromptNextLogin=1`
  } else if (authProvider === AUTH_PROVIDER.MS_BACKEND) {
    // Revoke the backend session (HttpOnly session cookie + CSRF; no bearer).
    // Best-effort — proceed with local cleanup even if it fails so the user is
    // never stuck locally "logged in". Land on the /logout confirmation screen
    // (like MSAL/Cognito) rather than the bare origin, which would drop straight
    // back into the boot saga; the user re-triggers login from that page's button.
    try {
      await withCsrf(logoutSession)
    } catch {
      /* proceed with local cleanup even if the backend call fails */
    }
    clearCsrfToken()
    sessionStorage.removeItem(MS_BACKEND_LOGIN_ATTEMPT_KEY)
    dispatch(logoutAuthUser())
    await persist.flush()
    clearPersistedAuthStorage()
    window.location.href = `${window.location.origin}/logout`
  } else {
    dispatch(logoutAuthUser())
    await persist.flush()
    clearPersistedAuthStorage()
    window.location.href = `${window.location.origin}/logout`
  }
}
